<template>
  <div class="trips">
    <toolbar
      backRoute="/dashboard"
      title="Trips"
      :addButton="true"
      addButtonLabel="Add trip"
      addButtonRoute="/dashboard/trips/new" />

    <d-row class="mt-5">
      <d-col cols="12" class="d-flex">
        <d-input
          v-model="filters.search"
          placeholder="Search trips"
          @keyup.native.enter="saveFilters" />

        <div :class="{ 'form-control': true, 'is-select': true, 'ml-2': true }">
          <v-select
            :options="[{ text: 'Cancelled trips', value: 1 }, { text: 'Active trips', value: 0 }]"
            v-model="filters.cancelled"
            :reduce="option => option.value"
            label="text"
            placeholder="All trips">
          </v-select>
        </div>

        <date-range-picker
          ref="picker"
          :locale-data="{
            'firstDay': 1,
            'format': 'dMMMyy'
          }"
          class="w-50 ml-2 text-center"
          :singleDatePicker="false"
          :timePicker="false"
          :showWeekNumbers="false"
          :showDropdowns="true"
          :autoApply="true"
          v-model="filters.dateRange"
          :ranges="false"
          :linkedCalendars="true">
          <template v-slot:input="picker" style="min-width: 350px;">
            <template v-if="picker.startDate === null || (picker.startDate.toString() !== picker.endDate.toString())">
              {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
            </template>
            <template v-if="picker.startDate !== null && picker.startDate.toString() === picker.endDate.toString()">
              {{ picker.startDate | dateStart }} - not specified
            </template>
          </template>
        </date-range-picker>

        <d-button
          theme="primary"
          outline
          class="ml-2"
          @click.prevent="saveFilters">
          Search
        </d-button>
        <d-button
          v-if="filters.search !== '' || filters.cancelled !== '' || filters.dateRange.startDate !== null || filters.dateRange.endDate !== null"
          theme="danger"
          outline
          class="ml-2"
          @click.prevent="resetFilters">
          Reset
        </d-button>
      </d-col>
    </d-row>

    <table class="table table-striped table-bordered table-hover mt-5">
      <thead>
        <tr>
          <sortable-table-heading field="reference" view="trips" :column="orderColumn" :direction="orderDirection">Reference</sortable-table-heading>
          <sortable-table-heading field="start_date" view="trips" :column="orderColumn" :direction="orderDirection">Start date</sortable-table-heading>
          <th>Status</th>
          <th>Locations</th>
          <th>Aircraft</th>
          <sortable-table-heading field="flights_count" view="trips" :column="orderColumn" :direction="orderDirection">Flights</sortable-table-heading>
          <th style="width: 120px;">Actions</th>
        </tr>
      </thead>

      <tbody v-if="!loadError">
        <tr v-if="!allDataLoaded">
          <td
            colspan="7"
            class="text-center">
            Loading trips&hellip;
          </td>
        </tr>
        <tr v-if="allDataLoaded && trips.length === 0">
            <td
              colspan="7"
              class="text-center">
              There are no trips to display&hellip;
            </td>
          </tr>
        <tr
          v-for="(trip, index) of trips"
          :key="'trip-' + index">
          <td class="align-middle">{{ trip.reference }}</td>
          <td class="align-middle text-uppercase" style="width: 160px;">{{ trip.start_date }}</td>
          <td class="align-middle text-uppercase" style="width: 100px;">
            <span v-if="trip.cancelled" class="text-danger">Cancelled</span>
            <span v-else class="text-primary">Active</span>
          </td>
          <td class="align-middle" style="width: 300px;"><small>{{ trip.locations }}</small></td>
          <td class="align-middle" style="width: 100px;">{{ trip.aircraft_regin_mark }}</td>
          <td class="align-middle text-center" style="width: 120px;">{{ trip.flights_count }}</td>
          <td class="align-middle" style="width: 180px;">
            <d-button
              theme="primary"
              @click.native="$router.push('/dashboard/trips/' + trip.id)"
              class="mr-2"
              title="Edit trip">
              <i class="material-icons">edit</i>
            </d-button>
            <d-button
              theme="danger"
              :disabled="removeInProgress"
              @click.prevent="removeTrip(trip.id)"
              title="Remove trip"
              class="mr-2">
              <i class="material-icons">delete</i>
            </d-button>
            <d-button
              v-if="!trip.cancelled"
              theme="warning"
              :disabled="cancelInProgress"
              @click.prevent="cancelTrip(trip.id)"
              title="Cancel trip">
              <i class="material-icons">cancel</i>
            </d-button>
            <d-button
              v-if="trip.cancelled"
              theme="warning"
              :disabled="restoreInProgress"
              @click.prevent="restoreTrip(trip.id)"
              title="Restore trip">
              <i class="material-icons">backspace</i>
            </d-button>
          </td>
        </tr>
      </tbody>
    </table>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      An error occurred during data loading. <a href="javascript:window.location.reload();" class="alert-link">Reload the page</a> to try again.
    </d-alert>

    <pagination :meta-data="paginationMetaData" />
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import DateUtils from './../utils/DateUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import SortableTableHeading from './../components/common/SortableTableHeading.vue';

export default {
  name: 'trips',
  hasDateRangeFilter: true,
  additionalFilters: {
    cancelled: null
  },
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination,
    'sortable-table-heading': SortableTableHeading
  },
  filters: {
    filterValidTo (validToDate) {
      if (validToDate === null) {
        return 'not specified';
      }

      return validToDate;
    },
    dateStart (input) {
      if (input === null) {
        return ' from ';
      }

      let date = [
        input.getDate(),
        DateUtils.getMonthShortName(input.getMonth() + 1),
        input.getFullYear().toString().substr(2, 2)
      ].map(n => n < 10 ? '0' + n : n).join('');

      return date;
    },
    dateEnd (input) {
      if (input === null) {
        return ' to ';
      }

      let date = [
        input.getDate(),
        DateUtils.getMonthShortName(input.getMonth() + 1),
        input.getFullYear().toString().substr(2, 2)
      ].map(n => n < 10 ? '0' + n : n).join('');

      return date;
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    }
  },
  data () {
    return {
      loaded: false,
      trips: null,
      loadError: false,
      removeInProgress: false,
      cancelInProgress: false,
      restoreInProgress: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('trips-list-reload', this.loadFilteredData);
  },
  methods: {
    removeTrip (tripID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/trips/delete/',
        id: tripID,
        successTitle: 'Trip removed',
        successText: 'The selected trip has been removed',
        confirmTitle: 'Do you really want to remove this trip?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    cancelTrip (tripID) {
      this.cancelInProgress = true;

      ListUtils.modifyItem({
        $http: this.$http,
        endpointBase: '/trips/update/',
        id: tripID,
        params: {
          cancelled: 1
        },
        successTitle: 'Trip cancelled',
        successText: 'The selected trip has been cancelled',
        confirmTitle: 'Do you really want to cancel this trip?',
        confirmText: '',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.cancelInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.cancelInProgress = false;
        }
      });
    },
    restoreTrip (tripID) {
      this.restoreInProgress = true;

      ListUtils.modifyItem({
        $http: this.$http,
        endpointBase: '/trips/update/',
        id: tripID,
        params: {
          cancelled: 0
        },
        successTitle: 'Trip restored',
        successText: 'The selected trip has been restored',
        confirmTitle: 'Do you really want to restore this trip?',
        confirmText: '',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.restoreInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.restoreInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('trips-list-reload', this.loadFilteredData);
  }
};
</script>

<style lang="scss" scoped>
.trips {
  .vue-daterange-picker {
    /deep/ .form-control {
      padding: 7px 10px;
    }
  }
}
</style>
